import i18next from 'i18next'
import { t } from '$i18n'
import { toLocale } from '$func/text'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

// change date to `hours:min`
export const formatClock = (date: Date) => {
  return date.toLocaleString(i18next.language, {
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  })
}

const DAY_MILLISECOND = 24 * 60 * 60 * 1000

export const areSameYear = (date1: Date, date2: Date) => {
  const year1 = date1.toLocaleDateString(i18next.language, { year: 'numeric' })
  const year2 = date2.toLocaleDateString(i18next.language, { year: 'numeric' })

  return year1 === year2
}

export const formatTime = (date: Date) => {
  const now = new Date()

  const diff = +now - +date

  // today => 'hour:min'
  if (diff < DAY_MILLISECOND && now.getDay() === date.getDay())
    return formatClock(date)

  // yesterday => 'Yesterday, hour:min'
  if (diff < 2 * DAY_MILLISECOND && now.getDay() === (date.getDay() + 1) % 7)
    return `${t('common.yesterday')}، ${toLocale(formatClock(date))}`

  //  in week => 'day name'
  if (diff < 7 * DAY_MILLISECOND)
    return date.toLocaleString(i18next.language, {
      weekday: 'long',
    })

  // in month => 'day MONTH[0:3]'
  if (areSameYear(now, date))
    return date.toLocaleString(i18next.language, {
      day: 'numeric',
      month: 'short',
    })

  // in year => 'MONTH[0:3] year'
  return date.toLocaleString(i18next.language, {
    month: 'short',
    year: 'numeric',
  })
}

export const formatToLocaleDateTime = (date: Date) => {
  const clockFormatter = new Intl.DateTimeFormat('fa-ir', {
    hour: '2-digit',
    minute: '2-digit',
  })
  const dateFormatter = new Intl.DateTimeFormat('fa-ir', {
    month: 'long',
    day: 'numeric',
  })
  const clock = clockFormatter.format(date)
  const dayMonth = dateFormatter.format(date)

  return `${dayMonth} - ${clock}`
}

export const formatToTimeDay = (date: Date) => {
  const clockFormatter = new Intl.DateTimeFormat('fa-ir', {
    hour: '2-digit',
    minute: '2-digit',
  })
  const dateFormatter = new Intl.DateTimeFormat('fa-ir', {
    month: 'long',
    day: 'numeric',
  })
  const clock = clockFormatter.format(date)
  const dayMonth = dateFormatter.format(date)

  return t('common.dateFormats.timeDay')(dayMonth, clock)
}

export const timeSince = (date: Date) => {
  const seconds = Math.floor((+new Date() - +date) / 1000)

  let interval = seconds / 31536000

  if (interval > 1) {
    return t('common.dateFormats.nYearsAgo')(toLocale(Math.floor(interval)))
  }
  interval = seconds / 2592000
  if (interval > 1) {
    return t('common.dateFormats.nMonthsAgo')(toLocale(Math.floor(interval)))
  }
  interval = seconds / 86400
  if (interval > 1) {
    return t('common.dateFormats.nDaysAgo')(toLocale(Math.floor(interval)))
  }
  interval = seconds / 3600
  if (interval > 1) {
    return t('common.dateFormats.nHoursAgo')(toLocale(Math.floor(interval)))
  }
  interval = seconds / 60
  if (interval > 1) {
    return t('common.dateFormats.nMinutesAgo')(toLocale(Math.floor(interval)))
  }
  return t('common.dateFormats.now')
}

/**
 * @returns date2 - date1
 */
export const calculateDatesDiff = (date1: Date, date2: Date) => {
  const delta = Math.abs(Number(date2) - Number(date1)) / 1000
  return calculateDateMetaFromSeconds(delta)
}

export const calculateDateMetaFromSeconds = (seconds: number) => {
  let delta = seconds

  // calculate (and subtract) whole days
  const days = Math.floor(delta / 86400)
  delta -= days * 86400

  // calculate (and subtract) whole hours
  const hours = Math.floor(delta / 3600) % 24
  delta -= hours * 3600

  // calculate (and subtract) whole minutes
  const minutes = Math.floor(delta / 60) % 60
  delta -= minutes * 60

  // what's left is seconds
  const s = Math.floor(delta % 60)

  return {
    days,
    hours,
    minutes,
    seconds: s,
  }
}

export const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000 // 86400000

export const isRecentDate = (date: Date) => {
  const now = new Date()
  const yesterdayDate = new Date(
    dateWithOffset() - DAY_IN_MILLISECONDS,
  ).toDateString()
  const isYesterday = yesterdayDate === date.toDateString()
  const isToday = now.toDateString() === date.toDateString()
  const isThisYear = now.getFullYear() === date.getFullYear()
  return { isToday, isYesterday, isThisYear }
}
